import {PubSub} from '@mamdev/cad.static-next-lib';

type ViewportName = 'mobile' | 'tablet' | 'desktop' | 'large' | 'large1440' | 'large1680' | 'large1920';

export const viewportBreakpointSizes: Record<ViewportName, number> = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
  large: 1280,
  large1440: 1440,
  large1680: 1680,
  large1920: 1920
};

class Viewport {
  smoothScrollSupported_: boolean;

  constructor() {
    this.smoothScrollSupported_ = 'scrollBehavior' in document.documentElement.style;
  }

  getCurrentViewport(): ViewportName {
    const viewports: ViewportName[] = ['large1920', 'large1680', 'large1440', 'large', 'desktop', 'tablet'];

    for (const viewport of viewports) {
      if (this.largerThan(viewport)) {
        return viewport;
      }
    }

    return 'mobile';
  }

  getElementYOffset(element: HTMLElement): number {
    return element.getBoundingClientRect().top + window.scrollY;
  }

  isNearOrInsideViewPort(element: HTMLElement, pixelsOffset = 300): boolean {
    const rect = element.getBoundingClientRect();
    return !(rect.bottom < (0 - pixelsOffset) || rect.right < 0 || rect.left > window.innerWidth || rect.top > (window.innerHeight + pixelsOffset));
  }

  isElementInViewport(element: HTMLElement, threshold = 200): boolean {
    const rect = element.getBoundingClientRect();

    return (
      rect.top >= -threshold
      && rect.left >= -threshold
      && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + threshold
      && rect.right <= (window.innerWidth || document.documentElement.clientWidth) + threshold
    );
  }

  largerThan(viewport: ViewportName): boolean {
    return window.innerWidth >= viewportBreakpointSizes[viewport];
  }

  matches(viewport: ViewportName): boolean {
    return this.largerThan(viewport);
  }

  isMobile(): boolean {
    return !this.largerThan('tablet');
  }

  isTablet(): boolean {
    return this.largerThan('tablet') && !this.largerThan('desktop');
  }

  isDesktop(): boolean {
    return this.largerThan('desktop');
  }

  isInAppView(): boolean {
    return window.ui.pageinfo.deviceclient === 'app';
  }

  scrollViewPortToTop() {
    if (this.smoothScrollSupported_) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo(0, 0);
    }
  }

  scrollViewPortTo(targetElement: Element, offset = 0) {
    const oTop = targetElement ? targetElement.getBoundingClientRect().top + window.scrollY : -offset;
    const windowTop = targetElement ? oTop - offset : oTop;

    if (this.smoothScrollSupported_) {
      window.scrollTo({
        top: windowTop,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo(0, windowTop);
    }
  }

  scrollToElement(element: Element, offset = 0) {
    return this.scrollViewPortTo(element, offset);
  }

  getLayoutClassForCurrentViewPortWidth(): string {
    if (this.matches('desktop')) {
      return 'b';
    } else if (this.matches('tablet')) {
      return 'm';
    } else {
      return 's';
    }
  }

  hideScrollbar() {
    document.documentElement.classList.remove('addScrolling');
    document.documentElement.classList.add('hideScrolling');
    PubSub.publish('sticky:resizeWidth');
  }

  showScrollbar() {
    document.documentElement.classList.remove('hideScrolling');
    document.documentElement.classList.add('addScrolling');
    PubSub.publish('sticky:resizeWidth');
  }
}

export default new Viewport();
